/**
 * SSE streaming client for Avatar chat replies.
 * Gateway events: message.started | token | message.completed (+ error shapes).
 */

import { appConfig, log } from '../config/env';
import type {
  MessageCompletedData,
  MessageStartedData,
  StreamChunk,
  TokenData,
} from '../types/api';
import type { WidgetChannel } from '../types/bridge';
import { embedGatewayHeaders } from './configHelpers';
import { WidgetApiError } from './errors';
import { isRecord } from './typeGuards';

export type { StreamChunk };

export interface StreamOptions {
  accessToken: string;
  message: string;
  /** Uploaded files (paths from presign). `message` may be empty when present. */
  attachments?: { path: string; file_name: string }[];
  /** `Channel` header — `web_widget` | `mobile_webview`. */
  channel?: WidgetChannel;
  /** Native SDK bundle / package — body `app_bundle_id` (header optional). */
  bundleId?: string;
  signal?: AbortSignal;
  /** Override for tests / harness */
  endpoint?: string;
  onChunk: (chunk: StreamChunk) => void;
}

/**
 * Streams an Avatar reply via text/event-stream.
 * Falls back to mock stream only when enableMockSse is true and strictSse is false.
 */
export async function streamAvatarReply(options: StreamOptions): Promise<void> {
  const endpoint =
    options.endpoint || `${appConfig.apiBaseUrl}/v1/chat/messages`;
  const channel = options.channel ?? 'web_widget';

  try {
    log('debug', 'Opening SSE stream', {
      endpoint,
      channel,
    });

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${options.accessToken}`,
        Channel: channel,
        ...embedGatewayHeaders({
          channel,
          bundleId: options.bundleId,
        }),
      },
      body: JSON.stringify({
        message: options.message,
        ...(options.attachments?.length
          ? { attachments: options.attachments }
          : {}),
        ...(channel === 'mobile_webview' && options.bundleId
          ? { app_bundle_id: options.bundleId }
          : {}),
      }),
      signal: options.signal,
    });

    if (!response.ok || !response.body) {
      let reason = `Stream unavailable (${response.status})`;
      let code = 'http_error';
      try {
        const errBody: unknown = await response.json();
        if (isRecord(errBody)) {
          if (typeof errBody.error === 'string') code = errBody.error;
          if (typeof errBody.message === 'string') reason = errBody.message;
          else if (typeof errBody.error === 'string') reason = errBody.error;
        }
      } catch {
        // keep status-based reason
      }
      throw new WidgetApiError(code, reason, response.status);
    }

    await parseEventStream(response.body, options.onChunk, options.signal);
  } catch (error) {
    if (options.signal?.aborted) return;

    const canMock = appConfig.enableMockSse && !appConfig.strictSse;
    if (canMock) {
      log('warn', 'Live SSE unavailable — using mock stream', error);
      await mockStream(options.message, options.onChunk, options.signal);
      return;
    }

    // Gateway refusals (e.g. invalid_attachment) go to the caller with their code.
    if (error instanceof WidgetApiError) throw error;

    const reason =
      error instanceof Error ? error.message : 'Unable to reach Avatar service';
    log('error', 'SSE stream failed', reason);
    options.onChunk({ type: 'error', reason });
  }
}

async function parseEventStream(
  body: ReadableStream<Uint8Array>,
  onChunk: (chunk: StreamChunk) => void,
  signal?: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finished = false;

  try {
    while (true) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        const chunk = mapSseBlock(part);
        if (!chunk) continue;
        onChunk(chunk);
        if (chunk.type === 'done' || chunk.type === 'error') {
          finished = true;
          return;
        }
      }
    }
    if (!finished) {
      onChunk({ type: 'done' });
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Parses one SSE block (event + data lines) into a StreamChunk.
 * Supports gateway named events and legacy `{ type, text }` / `[DONE]` shapes.
 */
function mapSseBlock(part: string): StreamChunk | null {
  const lines = part.split('\n');
  let eventName = '';
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }

  const raw = dataLines.join('\n');
  if (!raw || raw === ': keepalive') return null;

  if (raw === '[DONE]') {
    return { type: 'done' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Plain-text token fallback
    if (!eventName || eventName === 'token' || eventName === 'message') {
      return { type: 'token', text: raw };
    }
    return null;
  }

  if (eventName === 'message.started' && isRecord(parsed)) {
    const data = parsed as Partial<MessageStartedData>;
    return {
      type: 'started',
      conversationId:
        typeof data.conversation_id === 'string'
          ? data.conversation_id
          : undefined,
      sessionId:
        typeof data.session_id === 'string' ? data.session_id : undefined,
      avatarId: typeof data.avatar_id === 'string' ? data.avatar_id : undefined,
    };
  }

  if (eventName === 'token' && isRecord(parsed)) {
    const data = parsed as Partial<TokenData>;
    return {
      type: 'token',
      text: typeof data.content === 'string' ? data.content : '',
    };
  }

  if (eventName === 'message.completed' && isRecord(parsed)) {
    const data = parsed as Partial<MessageCompletedData>;
    return {
      type: 'done',
      text: typeof data.content === 'string' ? data.content : undefined,
    };
  }

  if (
    (eventName === 'error' || eventName === 'message.error') &&
    isRecord(parsed)
  ) {
    const message =
      (typeof parsed.message === 'string' && parsed.message) ||
      (typeof parsed.reason === 'string' && parsed.reason) ||
      (typeof parsed.error === 'string' && parsed.error) ||
      'Stream error';
    return { type: 'error', reason: message };
  }

  // Legacy proposal-style chunks without event: lines
  if (isRecord(parsed) && typeof parsed.type === 'string') {
    if (parsed.type === 'token') {
      const text =
        (typeof parsed.text === 'string' && parsed.text) ||
        (typeof parsed.content === 'string' && parsed.content) ||
        '';
      return { type: 'token', text };
    }
    if (parsed.type === 'done') {
      return {
        type: 'done',
        text: typeof parsed.text === 'string' ? parsed.text : undefined,
        conversationId:
          typeof parsed.conversationId === 'string'
            ? parsed.conversationId
            : undefined,
        sessionId:
          typeof parsed.sessionId === 'string' ? parsed.sessionId : undefined,
        avatarId:
          typeof parsed.avatarId === 'string' ? parsed.avatarId : undefined,
      };
    }
    if (parsed.type === 'error') {
      return {
        type: 'error',
        reason:
          (typeof parsed.reason === 'string' && parsed.reason) ||
          (typeof parsed.message === 'string' && parsed.message) ||
          'Stream error',
      };
    }
    if (parsed.type === 'started') {
      return {
        type: 'started',
        conversationId:
          typeof parsed.conversationId === 'string'
            ? parsed.conversationId
            : undefined,
        sessionId:
          typeof parsed.sessionId === 'string' ? parsed.sessionId : undefined,
        avatarId:
          typeof parsed.avatarId === 'string' ? parsed.avatarId : undefined,
      };
    }
  }

  if (isRecord(parsed) && typeof parsed.content === 'string' && !eventName) {
    return { type: 'token', text: parsed.content };
  }

  return null;
}

async function mockStream(
  userMessage: string,
  onChunk: (chunk: StreamChunk) => void,
  signal?: AbortSignal,
): Promise<void> {
  const reply =
    `Thanks for asking about "${userMessage.trim()}". ` +
    `I'm Avatar AI — I can help you discover, create, and get the most out of your Zetrix Avatar. ` +
    `(${appConfig.env} mock stream — set VITE_AVATAR_API_URL for live SSE.)`;

  onChunk({
    type: 'started',
    conversationId: 'conv_mock',
    sessionId: 'sess_mock',
    avatarId: 'av_mock',
  });

  const words = reply.split(' ');
  for (let i = 0; i < words.length; i++) {
    if (signal?.aborted) return;
    await delay(28);
    const word = words[i] ?? '';
    onChunk({
      type: 'token',
      text: (i === 0 ? '' : ' ') + word,
    });
  }
  onChunk({ type: 'done', conversationId: 'conv_mock' });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
